/* eslint-disable no-nested-ternary */
import Loading from '@gp/components/Loading';
import NoData from '@gp/components/NoData';
import FixedBottomScrollbar from '@home/components/FixedBottomScrollbar';
import classnames from 'classnames';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Table, TableAddon, TableColumn } from 'tea-component';
import CalendarPanelTopbar from '../../../CalendarPanel/components/CalendarPanelTopbar';
import Header from './Header';
// import Prejects from './Prejects';
import ProjectColumnItem from '@gp/components/ProjectColumnItem';
import useCurrentScroll from './_hooks/useCurrentScroll';
import { PROJECT_COLUMN_WIDTH, getDateColumns, getProjectNumText } from './utils';

import type { ICalendarTableDTO } from '@gp/types/calendar';
import type { IBaseProps } from '@home/types/common';

import './index.scss';

interface ICalendarTableProps extends IBaseProps {
  isLoading?: boolean;
  isNoData?: boolean;
  isFullScreen?: boolean;
  fullScreenContent: React.RefObject<HTMLDivElement | null>;
  tableWidth: number;
  dateType: ScheduledateTypes;
  data: ICalendarTableDTO[];
  // 工具栏左侧的内容
  topBarLeftElement?: React.ReactElement | null;
  // 工具栏右侧的内容
  topBarRightElement?: React.ReactElement | null;
}

const { scrollable } = Table.addons;

const getRecordKey = (record: ICalendarTableDTO): string => {
  const { project_id: projectId } = record || {};
  return projectId;
};

const CalendarTable: React.FC<ICalendarTableProps> = (props) => {
  const {
    style,
    className,
    isLoading = true,
    isNoData = true,
    isFullScreen,
    fullScreenContent,
    tableWidth,
    dateType,
    data,
    topBarLeftElement,
    topBarRightElement,
    reSetFun,
    isSingLine,
  } = props;

  //   因为表格自身有左右10px间距，而tableWidth是里面的宽度，所以要加20px
  const tableWidths = tableWidth + 20;

  const table = useRef<HTMLDivElement>(null);

  const addons = useMemo<TableAddon<ICalendarTableDTO>[]>(
    () => [
      scrollable({
        minWidth: 1200,
        maxHeight: isFullScreen ? 'calc(100vh - 180px)' : '',
      }),
    ],
    [isFullScreen],
  );

  const columns = useMemo<TableColumn<ICalendarTableDTO>[]>(() => {
    const dateColumns = getDateColumns(data, { tableWidths, fullScreenContent });
    return [
      {
        key: 'project_name',
        fixed: 'left',
        width: PROJECT_COLUMN_WIDTH,
        header: <Header align="left" title={data?.length} text={getProjectNumText(data?.length)} />,
        render: (cvm) => (
          <ProjectColumnItem data={cvm} isShowTextStudio={true} isShowInfoList={false} />
        ),
      },
      ...dateColumns,
    ];
  }, [tableWidths, fullScreenContent, data]);

  // 底部阴影
  const [shadow, setShadow] = useState(false);

  const handlePageScroll = (e: any) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    // 当滚动到底部时，距离底部不超过一个可视区域的高度 30是阴影本身 多24+20的间距 所以是74
    if (scrollTop + clientHeight >= scrollHeight - 72) {
      setShadow(false);
    } else {
      setShadow(true);
    }
  };

  const { currentDateLeft, currentDateWidth, disabled, visible, currentBtnType, onScroll2Current } =
    useCurrentScroll({
      table,
      isLoading,
      isNoData,
      dateType,
      columns,
    });

  const scrollbarDeps = useMemo<React.DependencyList>(
    () => [isLoading, isNoData, isFullScreen, currentDateLeft, currentDateWidth],
    [isLoading, isNoData, isFullScreen, currentDateLeft, currentDateWidth],
  );

  useEffect(() => {
    const dom = isFullScreen
      ? table.current?.querySelector('.tea-table__body')
      : document.querySelector('.dbg-body .tea-layout__content-body');

    // 初始化判断阴影
    if ((dom?.scrollHeight || 0) > (dom?.clientHeight || 0)) {
      setShadow(true);
    }
    // 滚动监听阴影
    dom?.addEventListener('scroll', handlePageScroll);
    return () => {
      dom?.removeEventListener('scroll', handlePageScroll);
    };
  }, [isFullScreen]);

  const noDataTextCur = useMemo(() => {
    return (
      <div className="dbg-nodata-div">
        <span className="dbg-nodata-normal-span">{`No data found for the selected filters. Please adjust your filters or click '`}</span>
        <span className="dbg-nodata-reset-a-span" onClick={reSetFun}>
          {'Reset'}
        </span>
        <span className="dbg-nodata-normal-span">{`' to turn all filters off.`}</span>
      </div>
    );
  }, [reSetFun]);

  // show-shadow 页面滚动底部消失
  return (
    <div
      style={style}
      className={classnames('dbg-calendar-table-context', className, {
        // 'show-shadow': true,
        'show-shadow': shadow,
        'dbg-calendar-table-full': isFullScreen,
      })}
    >
      <CalendarPanelTopbar
        // className="dbg-current-and-sign--right no-shadow"
        className="no-shadow"
        disabled={disabled}
        visible={visible}
        dateType={dateType}
        currentBtnType={currentBtnType as any}
        onScroll2Current={onScroll2Current}
        left={topBarLeftElement}
        right={topBarRightElement}
        isSingLine={isSingLine}
      />
      {isLoading ? (
        <Loading loading style={{ height: 200 }} />
      ) : isNoData ? (
        <NoData text={noDataTextCur} height={200} constomStyle={{ fontSize: 14 }} />
      ) : (
        <>
          <Table
            ref={table}
            // dbg-table-sticky 公共吸顶类名
            className={classnames('dbg-calendar-table dbg-table-sticky', {
              'dbg-calendar-table-is-sing-line': isSingLine,
            })}
            bordered="all"
            verticalTop={!!isFullScreen}
            addons={addons}
            columns={columns}
            recordKey={getRecordKey}
            records={data}
          />
          {/* 模拟滚动条 */}
          <FixedBottomScrollbar
            style={{ bottom: isFullScreen ? 8 : 10, marginTop: -9 }}
            initialLeft={currentDateLeft}
            deps={scrollbarDeps}
            getScrollableEl={() => {
              const tableEl = table.current;
              return tableEl?.querySelector('.tea-table__body') || null;
            }}
          />
        </>
      )}
    </div>
  );
};

export default CalendarTable;
